import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, X, Trash2, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function AdminRecensioni() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [deleteId, setDeleteId] = useState(null);

  const fetchAll = async () => {
    try {
      const res = await axios.get(`${API}/reviews?approved_only=false`);
      setReviews(res.data);
    } catch { toast.error('Errore nel caricamento'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const toggleApproved = async (r) => {
    try {
      await axios.put(`${API}/reviews/${r.id}`, { approved: !r.approved });
      toast.success(r.approved ? 'Recensione nascosta' : 'Recensione approvata!');
      fetchAll();
    } catch { toast.error('Errore'); }
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/reviews/${id}`); toast.success('Recensione eliminata'); setDeleteId(null); fetchAll(); }
    catch { toast.error("Errore nell'eliminazione"); }
  };

  const filtered = reviews.filter(r => {
    if (filter === 'approved') return r.approved;
    if (filter === 'pending') return !r.approved;
    return true;
  });

  const fmtDate = (d) => new Date(d).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });

  const Stars = ({ n }) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => <Star key={i} className={`w-4 h-4 ${i <= n ? 'text-accent fill-accent' : 'text-gray-200'}`} />)}
    </div>
  );

  return (
    <div className="min-w-0 w-full">
      <div className="mb-5">
        <h2 className="font-heading text-xl font-bold text-gray-900">Recensioni</h2>
        <p className="font-body text-gray-500 text-xs mt-0.5">
          {reviews.filter(r => r.approved).length} approvate · {reviews.filter(r => !r.approved).length} in attesa
        </p>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {[['all', 'Tutte'], ['pending', 'In attesa'], ['approved', 'Approvate']].map(([val, lbl]) => (
          <button
            key={val}
            onClick={() => setFilter(val)}
            className={`px-3 py-1.5 rounded-lg font-body text-xs font-medium transition-colors ${filter === val ? 'bg-primary text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
          >
            {lbl}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="bg-white rounded-xl h-20 animate-pulse border border-gray-100" />)}</div>
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => (
            <div key={r.id} className={`bg-white rounded-xl p-3 border-l-4 ${r.approved ? 'border-l-green-400' : 'border-l-orange-400'} border border-gray-100`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <span className="font-body font-semibold text-gray-900">{r.name}</span>
                    <Stars n={r.rating} />
                    <span className="font-body text-xs text-gray-400">{fmtDate(r.created_at)}</span>
                    <span className={`inline-block px-2.5 py-0.5 rounded-full font-body text-xs font-medium ${r.approved ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-600'}`}>
                      {r.approved ? 'Approvata' : 'In attesa'}
                    </span>
                  </div>
                  <p className="font-body text-sm text-gray-600 leading-relaxed">{r.comment}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => toggleApproved(r)}
                    className={`p-2 rounded-xl transition-colors ${r.approved ? 'hover:bg-orange-50 text-orange-500' : 'hover:bg-green-50 text-green-600'}`}
                    title={r.approved ? 'Nascondi' : 'Approva'}
                  >
                    {r.approved ? <X className="w-5 h-5" /> : <Check className="w-5 h-5" />}
                  </button>
                  <button onClick={() => setDeleteId(r.id)} className="p-2 hover:bg-red-50 rounded-xl text-red-500 transition-colors">
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="text-center py-16 text-gray-400 font-body bg-white rounded-2xl border border-gray-100">Nessuna recensione trovata</div>}
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="font-heading text-lg font-bold mb-2">Elimina Recensione</h3>
            <p className="font-body text-gray-500 mb-6">Sei sicuro? Questa azione \u00e8 irreversibile.</p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setDeleteId(null)} className="flex-1 rounded-xl">Annulla</Button>
              <Button onClick={() => handleDelete(deleteId)} className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-xl">Elimina</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
